/* eslint-disable react-hooks/rules-of-hooks */
import classNames from "classnames";
import classes from "./B2CCart.module.less";
import { B2CCartProcessTile } from "~/Cart/pages/CartPage/B2CCart/components/B2CCartProcessTile/B2CCartProcessTile";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import { SIZE_DESKTOP_S, SIZE_PHONE, SIZE_TABLET, useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { IconButton } from "@mui/material";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import ClearCartButton, { useClearCartAction } from "~/Cart/components/CartPage/Buttons/ClearCartButton";
import { EnterDesignationBtn } from "~/Cart/components/CartPage/Buttons/EnterDesignationButton";
import { canSeeWishlistButton } from "~/utils/features";
import AddCartToWishlistBtn from "~/Cart/components/CartPage/Buttons/AddCartToWishlistBtn";
import { Button } from "~/common/components/Button/Button";
import { useEffect, useRef, useState } from "react";
import { B2CProductList } from "~/Cart/pages/CartPage/B2CCart/components/B2CProductList/B2CProductList";
import { useDispatch, useSelector } from "react-redux";
import { type LineItem } from "~/Cart/models/CartData";
import { type ProductData } from "~/models/ProductData";
import { type RootState } from "~/redux/store";
import { deletePackageEntry, updatePackageEntry } from "~/Cart/redux/actions/cartQuoteCommonActions";
import { updateCartCommentThunk } from "~/Cart/redux/actions/cartPageActions";
import { useTranslation } from "react-i18next";
import { CART_STEP_2 } from "~/utils/url/staticUrls";
import { resolveUrl } from "~/utils/url/urlResolver";
import { CommentTargets, trackCommentActions } from "~/feature/Analytics/ga/salesPartner";
import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { CommentButton } from "~/QuoteCart/components/ActiveQuoteEdit/ProductTable/CommentButton";
import { CommentButton as LabeledCommentButton } from "~/Wishlist/page/WishlistPage/components/buttons/CommentButton";

interface Props {
  className?: string;
}

export const B2CCart = (props: Props) => {
  const { t } = useTranslation();
  const clearCart = useClearCartAction();

  const size = useNumericScreenSize();
  const isPhone = size <= SIZE_PHONE;
  const isMinTablet = size >= SIZE_TABLET;
  const isDesktop = size >= SIZE_DESKTOP_S;

  const [pinnedElRef, isPinned] = usIsPinnedDetector<HTMLDivElement>();

  const continueButton = <Button href={resolveUrl(CART_STEP_2)}>{t("page.cart.b2c.btn.continue")}</Button>;

  const items = useSelector(selectCartElements);
  const dispatch = useDispatch();
  const updateItem = (item: LineItem, changes: Partial<LineItem>) => {
    if (Object.entries(changes).flat()[0] === "comment") {
      trackCommentActions(
        changes.comment,
        item.comment,
        CommentTargets.CART_ITEM,
        changes?.comment?.length,
        item.productCode,
      );
    }
    dispatch(updatePackageEntry(item.id, changes));
  };
  const deleteItem = (item: LineItem) => {
    dispatch(deletePackageEntry(item.id));
  };

  const cartComment = useSelector(selectCartComment);
  const setCommentInstant = (comment: string) => dispatch(updateCartCommentThunk(comment, cartComment));

  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const toggleCommentOpen = () => setIsCommentOpen((prev) => !prev);
  const [commentRef, toggleComment] = useShowAndScrollInput(toggleCommentOpen);

  return (
    <div className={classNames(classes.root, props.className)}>
      <B2CCartProcessTile activeIndex={0} />
      <div className={classes.headerSection}>
        <PageTitle className={classes.pageTitle}>{t("page.cart.b2c.title")}</PageTitle>
        {isPhone && (
          <div className={classes.phoneActions}>
            <CommentButton open={isCommentOpen} onClick={toggleComment} fontSize={"small"} />
            <IconButton onClick={clearCart} color={"primary"}>
              <Icon type={IconType.Bin} />
            </IconButton>
          </div>
        )}
        <div className={classes.leftActions}>
          <EnterDesignationBtn />
          {canSeeWishlistButton() && <AddCartToWishlistBtn />}
          {isMinTablet && <ClearCartButton />}
          {isMinTablet && (
            <LabeledCommentButton isOpen={isCommentOpen} onClick={toggleComment} hasComment={!!cartComment} />
          )}
        </div>
        <div className={classNames(classes.rightActions, "gtm-begin-checkout")} hidden={!isMinTablet}>
          {continueButton}
        </div>
      </div>
      <div className={classes.cartComment} hidden={!isCommentOpen}>
        <CommentInput
          value={cartComment}
          onChange={setCommentInstant}
          label={t("page.cart.b2c.comment.title")}
          ref={commentRef}
        />
      </div>

      <B2CProductList items={items} onDeleteAll={clearCart} onChange={updateItem} onDelete={deleteItem} />

      <div className={classes.bottomContinue} hidden={!isMinTablet}>
        {continueButton}
      </div>

      <div
        ref={pinnedElRef}
        className={classNames(classes.phoneContinue, isPinned && classes.phoneContinuePinned)}
        hidden={isMinTablet}
      >
        {continueButton}
      </div>
    </div>
  );
};

const usIsPinnedDetector = <T extends HTMLElement>() => {
  const elRef = useRef<T>(null);
  const [isPinned, setIsPinned] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => setIsPinned(e.intersectionRatio < 1), { threshold: [1] });
    observer.observe(elRef.current!);

    return () => {
      observer.disconnect();
    };
  }, []);

  return [elRef, isPinned] as const;
};

interface CartElement {
  data: LineItem;
  product: ProductData;
}

const selectCartElements = (root: RootState) => {
  const cart = root.cart;
  const lineItems = cart.cartEntries
    .map((id) => cart.cartEntriesById[id])
    .flatMap((entry) => entry.packEntryIds.map((id) => cart.packEntriesById[id]));

  const cartElements: CartElement[] = lineItems.map((lineItem) => ({
    data: lineItem,
    product: root.products.basic[lineItem.productCode],
  }));
  return cartElements;
};

const selectCartComment = (root: RootState) => root.cart.comment;
